import { AiFieldType, DueDiligenceFunnelTierSectionPdfLayout } from '@/__generated__/graphql';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { ITier } from '@/components/dueDiligence-page/dueDiligenceRecord/types';
import Input from '@/components/basicComponents/input';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CREATE_DUE_DILIGENCE_TIER_MUTATION, GET_FUNNEL_QUERY } from '../queries';
import { saveOnEnter } from '../utils';

interface DuplicateTierProps {
  onClose: () => void;
  isOpen: boolean;
  currentTier: ITier | null;
  funnelId: string | null;
  numberOfTiers: number;
}

const DuplicateTier = ({ onClose, isOpen, currentTier, funnelId, numberOfTiers }: DuplicateTierProps) => {
  if (!currentTier || !funnelId) return <></>;
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [value, setValue] = useState('');

  const [createTier] = useMutation(CREATE_DUE_DILIGENCE_TIER_MUTATION, {
    refetchQueries: () => [
      {
        query: GET_FUNNEL_QUERY,
        variables: {
          id: queryParams.get('funnelId')
        }
      }
    ]
  });

  const saveValue = () => {
    const updatedSections = currentTier.sections.map((section) => {
      const updatedFields = section.fields.map((field) => {
        return {
          name: field.name,
          required: field.required,
          type: field.type as AiFieldType,
          linkedFieldId: null as null,
          promptGroup: field.promptGroup
            ? {
                prompts: field.promptGroup.prompts,
                summaryPrompt: field.promptGroup.summaryPrompt,
                contextPrompt: field.promptGroup.contextPrompt,
                rubricTemplates: field.promptGroup.rubricTemplates
              }
            : null
        };
      });
      return {
        name: section.name,
        userCanScore: section.userCanScore,
        pdfLayout: section.pdfLayout as DueDiligenceFunnelTierSectionPdfLayout,
        fields: updatedFields
      };
    });

    const updatedChecklistItems = currentTier.checklistItems.map((item) => {
      return { name: item.name, required: item.required };
    });

    createTier({
      variables: {
        data: {
          funnelId: funnelId,
          name: value,
          order: numberOfTiers + 1,
          sections: updatedSections,
          checklistItems: updatedChecklistItems
        }
      }
    });
    onClose();
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate Tier"
      confirmButton={{ name: 'Save', onClick: saveValue, disabled: !value }}
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        label="Name"
        type="text"
        placeholder="Give this Tier a unique name"
        size="lg"
        onKeyUp={(event) => saveOnEnter(event, saveValue, !value)}
        isRequiredField
        autoFocus
      />
    </ModalWrappedContent>
  );
};

export default DuplicateTier;
